/**
 * Card d'évolution des soldes par période de clôture (Fév 2026).
 * Utilise ComposedChart (Line pour solde de clôture + Bar pour flux net mensuel).
 * Alimenté par :
 *   - /api/bank/analytics/period-history?account_id=xxx&months=12  (compte)
 *   - /api/treasury/period-history?months=12&include_wave=true    (consolidé)
 *
 * Props :
 *   - title, subtitle
 *   - data: [{ period, opening_balance, closing_balance, credits, debits, net_flow, tx_count, variation_vs_prev_pct, closure_status }]
 *   - loading
 *   - onSelectPeriod(period): (optionnel) callback quand une barre est cliquée
 *   - selectedPeriod: (optionnel) période mise en évidence
 */
import React, { useMemo } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Lock } from 'lucide-react';

const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];
const shortMonth = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-');
  return `${MONTHS_FR[+m - 1]} ${y.slice(2)}`;
};
const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('fr-FR').format(Math.round(n)));

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg text-xs">
        <div className="font-semibold text-slate-800 mb-1.5">{d.label}
          {d.closure_status === 'closed' && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full">
              <Lock className="h-2.5 w-2.5" /> Clôturé
            </span>
          )}
        </div>
        <div className="space-y-0.5">
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Solde ouverture :</span>
            <span className="font-mono text-slate-700">{fmt(d.opening_balance)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Solde clôture :</span>
            <span className="font-mono font-semibold text-blue-700">{fmt(d.closing_balance)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-emerald-600">Crédits :</span>
            <span className="font-mono text-emerald-700">+{fmt(d.credits)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-rose-600">Débits :</span>
            <span className="font-mono text-rose-700">{fmt(d.debits)}</span>
          </div>
          <div className="flex justify-between gap-4 border-t border-slate-100 pt-1 mt-1">
            <span className="text-slate-500">Flux net :</span>
            <span className={`font-mono font-semibold ${d.net_flow >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {d.net_flow >= 0 ? '+' : ''}{fmt(d.net_flow)}
            </span>
          </div>
          {d.variation_vs_prev_pct != null && (
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">Var vs M-1 :</span>
              <span className={`font-mono ${d.variation_vs_prev_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {d.variation_vs_prev_pct >= 0 ? '+' : ''}{d.variation_vs_prev_pct}%
              </span>
            </div>
          )}
          <div className="text-[10px] text-slate-400 mt-1">{d.tx_count} tx</div>
        </div>
      </div>
    );
};

export default function PeriodEvolutionCard({
  title = 'Évolution du solde par clôture mensuelle',
  subtitle,
  data = [],
  loading = false,
  onSelectPeriod,
  selectedPeriod,
}) {
  const chartData = useMemo(() =>
    (data || []).map(d => ({
      period: d.period,
      label: shortMonth(d.period),
      closing_balance: d.closing_balance,
      opening_balance: d.opening_balance,
      credits: d.credits,
      debits: -Math.abs(d.debits || 0),  // afficher en négatif
      net_flow: d.net_flow,
      tx_count: d.tx_count,
      variation_vs_prev_pct: d.variation_vs_prev_pct,
      closure_status: d.closure_status,
    })),
  [data]);

  // Trouver dernière période avec closing_balance connu
  const last = chartData.filter(d => d.closing_balance != null).slice(-1)[0];
  const first = chartData.filter(d => d.closing_balance != null)[0];
  const totalVariation = last && first && first.closing_balance
    ? Math.round(((last.closing_balance - first.closing_balance) / Math.abs(first.closing_balance)) * 100 * 10) / 10
    : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="period-evolution-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-blue-600" />
          <div>
            <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
        </div>
        {totalVariation != null && (
          <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${totalVariation >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
            {totalVariation >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {totalVariation >= 0 ? '+' : ''}{totalVariation}% sur {chartData.length} mois
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Chargement…</div>
      ) : chartData.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Aucune donnée</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
            <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
            <ReTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="right" dataKey="credits" name="Crédits" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={30}
              onClick={(d) => onSelectPeriod?.(d.period)} cursor={onSelectPeriod ? 'pointer' : 'default'}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={selectedPeriod && d.period !== selectedPeriod ? 0.35 : 0.85} />
              ))}
            </Bar>
            <Bar yAxisId="right" dataKey="debits" name="Débits" fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={30}
              onClick={(d) => onSelectPeriod?.(d.period)} cursor={onSelectPeriod ? 'pointer' : 'default'}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={selectedPeriod && d.period !== selectedPeriod ? 0.35 : 0.85} />
              ))}
            </Bar>
            <Line yAxisId="left" type="monotone" dataKey="closing_balance" name="Solde de clôture" stroke="#3b82f6" strokeWidth={2.5}
              dot={{ r: 4, fill: '#3b82f6' }} activeDot={{ r: 6 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
